import { bookingValues, bookingModules } from '@/online_booking/new/helpers/constants';

export default {
	namespaced: true,
	state: {
		waitingListOnly: false,
		currentStep: '',
		currentModule: bookingModules.BOOK_APPOINTMENT, // setting default state, otherwise the application breaks
		onlineBooking2ndStep: '', // only this step is unique in all 3 different online booking settings.
		practitionerViewActive: false,
		paymentStepVisible: false,
		selectedValues: {...bookingValues},
		module_mapper : {
			// eg: search the implementation by `this.setCurrentModule(bookingModules.BOOK_VOUCHER);`
			[bookingModules.BOOK_APPOINTMENT]: 'bookAppointmentSteps',
			[bookingModules.BOOK_GIFT_CARD]: 'bookGiftCardSteps',
			[bookingModules.BOOK_EVENT]: 'bookEventSteps',
			[bookingModules.BOOK_VOUCHER]: 'bookVoucherSteps',
			[bookingModules.BOOK_COURSE]: 'bookCourseSteps',
			[bookingModules.BOOK_SUBSCRIPTION]: 'bookSubscriptionSteps',
		},
        calendarsCount: 0,
        settings: {},
    },

	getters: {
		hasAddonsAvailableToSelect: (state, getters, rootState, rootGetters) => {
			return rootGetters['hasAddonsAvailableToSelect'];
		},

		waitingListOnly: (state) => {
            return state.waitingListOnly;
        },

		initialBookAppointmentSteps: (state, getters) => {
			/**
			 * Scenarios:

			 * Scenario: 1
			 * 1. Calendar
			 * 2. Product
			 * 3. Addon Product
			 * 4. Date
			 * 5. Fill Information
			 * 6. Confirm Booking
			 * 7. Payment

			 * Scenario: 2
			 * 1. Product
			 * 2. Addon Product
			 * 3. Date
			 * 4. Calendar
			 * 5. Fill Information
			 * 6. Confirm Booking
			 * 7. Payment

			 * Scenario: 3
			 * 1. Product
			 * 2. Addon Product
			 * 3. Calendar
			 * 4. Date
			 * 5. Fill Information
			 * 6. Confirm Booking
			 * 7. Payment
			 */

			const productsStep = [
				{
					id: 'products',
					title: trans('global.product'),
					status: state.selectedValues.products,
					active: false,
					complete: false,
					hidden: false,
					icon: 'fa-regular fa-briefcase',
				},
				{
					id: 'addonProducts',
					title: trans('online_booking.addon_product'),
					status: state.selectedValues.addonProducts,
					active: false,
					complete: false,
					hidden: !getters.hasAddonsAvailableToSelect,
					icon: 'fa-regular fa-rectangle-history-circle-plus',
				}
			]
			const timeStep =  [
				{
					id: 'time',
					title: trans('online_booking.sidebar.date_time'),
					status: state.selectedValues.time,
					active: false,
					complete: false,
					hidden: state.waitingListOnly,
					icon: 'fa-regular fa-clock',
				},
			]
			const calendarStep = [
				{
					id: 'calendar',
					title: state.practitionerViewActive ? trans('online_booking.sidebar.practitioner') : trans('online_booking.calendar'),
					status: state.selectedValues.calendar,
					active: false,
					complete: false,
					hidden: false,
					icon: 'fa-regular fa-calendar',
				},
			]

			if (state.onlineBooking2ndStep === 'times') {
				// scenario 2
				return [
					...productsStep,
					...timeStep,
					...calendarStep,
				];
			} else if (state.onlineBooking2ndStep === 'calendar') {
				// scenario 3
				return [
					...productsStep,
					...calendarStep,
					...timeStep,
				];
			} else {
				// scenario 1
				return [
					...calendarStep,
					...productsStep,
					...timeStep,
				];
			}
		},

		bookAppointmentSteps: (state, getters) => [
			...getters.initialBookAppointmentSteps,
			{
				id: 'fillInformation',
				title: trans('online_booking.sidebar.your_details'),
				status: state.selectedValues.fillInformation,
				active: false,
				complete: false,
				hidden: false,
				icon: 'fa-regular fa-user',
			},
			{
				id: 'confirmation',
				title: trans('events.receipt'),
				status: null,
				active: false,
				complete: false,
				hidden: false,
				icon: null,
			},
			{
				id: 'payment',
				title: trans('online_payment.payment_heading'),
				status: null,
				active: false,
				complete: false,
				hidden: state.waitingListOnly || !state.paymentStepVisible,
				icon: null,
			},
		],

		bookGiftCardSteps: (state) => [
			{
				id: 'giftCard',
				title: trans('online_booking.sidebar.gift_card_details'),
				status: state.selectedValues.giftCard,
				active: false,
				complete: false,
				hidden: false,
				icon: 'fa-regular fa-gift-card',
			},
			{
				id: 'fillInformation',
				title: trans('online_booking.sidebar.your_details'),
				status: state.selectedValues.fillInformation,
				active: false,
				complete: false,
				hidden: false,
				icon: 'fa-regular fa-user',
			},
			{
				id: 'payment',
				title: trans('online_payment.payment_heading'),
				status: null,
				active: false,
				complete: false,
				hidden: !state.paymentStepVisible,
				icon: null,
			},
		],

		bookEventSteps: (state) => [
			{
				id: 'eventInfo',
				title: trans('online_booking.sidebar.event_details'),
				status: state.selectedValues.eventInfo,
				active: false,
				complete: false,
				hidden: false,
				icon: 'fa-regular fa-users',
			},
			{
				id: 'fillInformation',
				title: trans('online_booking.sidebar.your_details'),
				status: state.selectedValues.fillInformation,
				active: false,
				complete: false,
				hidden: false,
				icon: 'fa-regular fa-user',
			},
			{
				id: 'confirmation',
				title: trans('events.receipt'),
				status: null,
				active: false,
				complete: false,
				hidden: false,
				icon: null,
			},
			{
				id: 'payment',
				title: trans('online_payment.payment_heading'),
				status: null,
				active: false,
				complete: false,
				hidden: !state.paymentStepVisible,
				icon: null,
			},
		],

		bookVoucherSteps: (state) => [
			{
				id: 'voucher',
				title: trans('client.voucher_details'),
				status: state.selectedValues.voucher,
				active: false,
				complete: false,
				hidden: false,
				icon: 'fa-regular fa-receipt',
			},
			{
				id: 'fillInformation',
				title: trans('online_booking.sidebar.your_details'),
				status: state.selectedValues.fillInformation,
				active: false,
				complete: false,
				hidden: false,
				icon: 'fa-regular fa-user',
			},
			{
				id: 'confirmation',
				title: trans('events.receipt'),
				status: null,
				active: false,
				complete: false,
				hidden: false,
				icon: null,
			},
			{
				id: 'payment',
				title: trans('online_payment.payment_heading'),
				status: null,
				active: false,
				complete: false,
				hidden: !state.paymentStepVisible,
				icon: null,
			},
		],

		bookCourseSteps: (state) => [
			{
				// this step in course booking only appears in the sidebar,
				// but there is no component for this step, but it is used to redirect to the course details page
				id: 'courseInfo',
				title: trans('online_booking.sidebar.course_details'),
				status: state.selectedValues.courseInfo,
				active: false,
				complete: false,
				hidden: false,
				icon: 'fa-regular fa-graduation-cap',
			},
			{
				id: 'fillInformation',
				title: trans('online_booking.sidebar.your_details'),
				status: state.selectedValues.fillInformation,
				active: false,
				complete: false,
				hidden: false,
				icon: 'fa-regular fa-user',
			},
			{
				id: 'confirmation',
				title: trans('events.receipt'),
				status: null,
				active: false,
				complete: false,
				hidden: false,
				icon: null,
			},
			{
				id: 'payment',
				title: trans('online_payment.payment_heading'),
				status: null,
				active: false,
				complete: false,
				hidden: !state.paymentStepVisible,
				icon: null,
			},
		],

		bookSubscriptionSteps: (state) => [
			{
				// this step in course booking only appears in the sidebar,
				// but there is no component for this step, but it is used to redirect to the course details page
				id: 'subscriptionInfo',
				title: trans('online_booking.sidebar.subscription_details'),
				status: state.selectedValues.subscriptionInfo,
				active: false,
				complete: false,
				hidden: false
			},
			{
				id: 'fillInformation',
				title: trans('online_booking.sidebar.your_details'),
				status: state.selectedValues.fillInformation,
				active: false,
				complete: false,
				hidden: false
			},
			{
				id: 'confirmation',
				title: trans('events.receipt'),
				status: null,
				active: false,
				complete: false,
				hidden: false
			},
			{
				id: 'payment',
				title: trans('global.payment'),
				status: null,
				active: false,
				complete: false,
				hidden: !state.paymentStepVisible
			},
		],

		currentModuleSteps: (state) => {
			return state.module_mapper[state.currentModule];
		},

		filteredSteps: (state, getters) => {
			const visibleSteps = getters[getters.currentModuleSteps].filter(step => !step.hidden); // steps that aren't hidden from the sidebar
			const activeStepIndex = visibleSteps.findIndex(step => step.id === state.currentStep); // index of the active step
			// set the active step to true and the previous steps to complete
			return visibleSteps.map((step, index) => {
				if (index < activeStepIndex) {
					step.active = true;
					step.complete = true;
				} else if (index === activeStepIndex) {
					step.active = true;
					step.complete = false;
				} else {
					step.active = false;
					step.complete = false;
				}
				return step;
			});
		},

		currentStepNumber: (state, getters) => getters.filteredSteps.findIndex(step => step.id === state.currentStep) + 1,

		totalStepCount: (state, getters) => getters.filteredSteps.length,

		getPreviousStepId: (state, getters) => {
			const currentStepIndex = getters.currentStepNumber - 1;
			const previousStepNumber = currentStepIndex - 1;
			return previousStepNumber >= 0 ? getters.filteredSteps[previousStepNumber]?.id : null;
		},
	},

	mutations: {
		SET_CURRENT_STEP (state, payload) {
			state.currentStep = payload;
		},

		SET_PAYMENT_STEP_VISIBILITY (state, payload) {
			state.paymentStepVisible = payload;
		},

		SET_SELECTED_VALUE (state, payload) {
			const { key, value } = payload;
			state.selectedValues[key] = value;
		},

		SET_ONLINE_BOOKING_2ND_STEP (state, payload) {
			state.onlineBooking2ndStep = payload;
		},

		SET_CURRENT_MODULE (state, payload) {
			state.currentModule = payload;
		},

		SET_PRACTITIONER_VIEW (state, payload) {
			state.practitionerViewActive = payload;
		},

		SET_WAITING_LIST_ONLY (state, payload) {
			state.waitingListOnly = payload;
		},

		RESET_DEFAULTS (state) {
			state.selectedValues = {...bookingValues};
			state.paymentStepVisible = false;
		},

        SET_CALENDARS_COUNT(state, payload) {
            state.calendarsCount = payload;
        },

        SET_SETTINGS(state, payload) {
            state.settings = payload;
        },
	},

	actions: {
		setCurrentStep ({ commit }, currentStep) {
			commit('SET_CURRENT_STEP', currentStep);
		},

		setPaymentStepVisibility ({ commit }, paymentStepVisible) {
			commit('SET_PAYMENT_STEP_VISIBILITY', paymentStepVisible);
		},

		setWaitingListOnly ({ commit }, waitingListOnly) {
			commit('SET_WAITING_LIST_ONLY', waitingListOnly);
		},

		setSelectedValue ({ commit }, bookingStateData) {
			commit('SET_SELECTED_VALUE', bookingStateData);
		},

		setOnlineBooking2ndStep ({ commit }, settingValue) {
			commit('SET_ONLINE_BOOKING_2ND_STEP', settingValue);
		},

		setCurrentModule ({ commit, dispatch }, currentModule) {
			dispatch('resetDefaults');
			commit('SET_CURRENT_MODULE', currentModule);
		},

		setPractitionerView ({ commit }, practitionerView) {
			commit('SET_PRACTITIONER_VIEW', practitionerView);
		},

		resetDefaults ({ commit }) {
			commit('RESET_DEFAULTS');
		},

        setCalendarsCount({ commit }, calendarsCount) {
            /**
             * We need to auto-select the calendar if there is only one available (from online booking settings)
             * But if there are 2 calendars available in OB, and one calendar doesn't have any services, it will not be counted as a valid calendar.
             * So the total count of calendars will be 1, and we need still need to auto-select the available calendar.
             */
            commit('SET_CALENDARS_COUNT', calendarsCount);
        },

        setOnlineBookingSettings({ commit }, onlineBookingSettings) {
            commit('SET_SETTINGS', onlineBookingSettings);
        },
	},
};
